import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarClock,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  LogOut,
  Send,
  Mail,
  Trash2,
  ExternalLink,
  FileText,
  ArrowLeft,
} from "lucide-react";
import {
  adminLogout,
  adminMe,
  setAdminToken,
  getAdminToken,
  newsletterConfig,
  newsletterAudiences,
  newsletterList,
  newsletterGet,
  newsletterGenerate,
  newsletterUpdate,
  newsletterApprove,
  newsletterReject,
  newsletterPush,
  newsletterSchedule,
  newsletterSendNow,
  newsletterDelete,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";
import SEO from "@/components/SEO";

const STATUS_STYLES = {
  Draft: "bg-amber-50 text-amber-800 border-amber-200",
  Approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Pushed: "bg-sky-50 text-sky-800 border-sky-200",
  Scheduled: "bg-indigo-50 text-indigo-800 border-indigo-200",
  Sent: "bg-[#3A7A8C]/10 text-[#3A7A8C] border-[#3A7A8C]/20",
  Rejected: "bg-rose-50 text-rose-800 border-rose-200",
};

export default function AdminNewsletter() {
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [batchSize, setBatchSize] = useState(3);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editing, setEditing] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    adminMe()
      .then((res) => {
        if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at);
      })
      .catch(() => {
        setAdminToken("");
        nav("/admin/login", { replace: true });
      });
  }, [nav]);

  useSessionRenewal({
    onExpired: () => {
      setAdminToken("");
      nav("/admin/login", { replace: true });
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, lst] = await Promise.all([newsletterConfig(), newsletterList()]);
      setConfig(cfg);
      setNewsletters(lst.newsletters || []);
      setCounts(lst.counts || {});
      if (cfg.configured) {
        try {
          const a = await newsletterAudiences();
          setAudiences(a.audiences || []);
        } catch (e) {
          console.warn("audiences load failed:", e);
        }
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        setAdminToken("");
        nav("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load newsletters.");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    load();
  }, [load]);

  const openNewsletter = async (id) => {
    setSelectedId(id);
    try {
      const doc = await newsletterGet(id);
      setSelectedDoc(doc);
      setEditing({
        subject_line: doc.subject_line || "",
        preview_text: doc.preview_text || "",
        title: doc.title || "",
        body_markdown: doc.body_markdown || "",
      });
    } catch {
      toast.error("Could not load newsletter.");
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setSelectedDoc(null);
    setEditing({});
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await newsletterGenerate(batchSize);
      toast.success(`Generated ${res.generated} newsletter${res.generated === 1 ? "" : "s"}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const saveEdits = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const updated = await newsletterUpdate(selectedId, editing);
      setSelectedDoc(updated);
      toast.success("Saved.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const doAction = async (fn, msg, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try {
      await fn(selectedId);
      toast.success(msg);
      await openNewsletter(selectedId);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const doPush = async (audienceId) => {
    setBusy(true);
    try {
      const res = await newsletterPush(selectedId, audienceId ? { audience_id: audienceId } : {});
      toast.success(`Pushed to MailChimp (campaign ${res.campaign_id}).`);
      await openNewsletter(selectedId);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Push failed.");
    } finally {
      setBusy(false);
    }
  };

  const doSchedule = async () => {
    const iso = window.prompt(
      "Schedule send time (UTC ISO format, must end on :00/:15/:30/:45)\nExample: 2026-07-01T13:00:00Z"
    );
    if (!iso) return;
    setBusy(true);
    try {
      await newsletterSchedule(selectedId, iso);
      toast.success("Scheduled.");
      await openNewsletter(selectedId);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Schedule failed.");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this newsletter? This will also remove the MailChimp draft if one exists.")) return;
    setBusy(true);
    try {
      await newsletterDelete(selectedId);
      toast.success("Deleted.");
      closeDetail();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    try {
      await adminLogout();
    } catch (err) {
      console.warn("logout failed:", err);
    }
    setAdminToken("");
    nav("/admin/login", { replace: true });
  };

  return (
    <main data-testid="admin-newsletter-page" className="bg-white min-h-screen">
      <SEO title="Newsletter Console" path="/admin/newsletters" noIndex />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="ash-eyebrow">Ashlan Admin</span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#080808]">Newsletter Console</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/admin/blog"
              data-testid="nav-to-blog"
              className="text-sm text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5"
            >
              <FileText size={14} /> Blog console
            </Link>
            <button
              onClick={load}
              className="text-sm text-[#3C4048] hover:text-[#080808] inline-flex items-center gap-1.5"
              data-testid="reload-btn"
            >
              <RefreshCw size={14} /> Reload
            </button>
            <button
              onClick={onLogout}
              className="text-sm text-[#3C4048] hover:text-[#080808] inline-flex items-center gap-1.5"
              data-testid="logout-btn"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Status banner */}
        {config && !config.configured && (
          <div
            className="bg-amber-50 border border-amber-200 text-amber-900 p-4 mb-6 text-sm"
            data-testid="mailchimp-not-configured"
          >
            MailChimp is not configured on the backend. Set `MAILCHIMP_API_KEY` + `MAILCHIMP_DC` in
            `/app/backend/.env` to enable.
          </div>
        )}
        {config && config.configured && (
          <div
            className="bg-emerald-50 border border-emerald-200 text-emerald-900 p-4 mb-6 text-sm flex flex-wrap items-center gap-x-6 gap-y-1"
            data-testid="mailchimp-config-status"
          >
            <span>
              <strong>MailChimp connected.</strong> Default audience{" "}
              <code className="text-xs">{config.default_audience_id || "—"}</code>
            </span>
            <span>From: {config.default_from_name} &lt;{config.default_from_email}&gt;</span>
            <span>{audiences.length} audience{audiences.length === 1 ? "" : "s"}</span>
          </div>
        )}

        {/* Counts strip */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="counts-strip">
          {["Draft", "Approved", "Pushed", "Scheduled", "Sent", "Rejected"].map((s) => (
            <div key={s} className="border border-[#e5e7eb] bg-white p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5A5F6A] font-semibold">{s}</div>
              <div className="text-2xl font-bold text-[#080808] mt-1" data-testid={`count-${s.toLowerCase()}`}>
                {counts[s] || 0}
              </div>
            </div>
          ))}
        </div>

        {/* Generate batch */}
        <div className="flex flex-wrap items-end gap-4 mb-8 p-4 bg-[#F5F5F5] border border-[#e5e7eb]">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-[#5A5F6A] font-semibold mb-2">
              Batch size
            </label>
            <input
              type="number"
              min="1"
              max="6"
              value={batchSize}
              onChange={(e) => setBatchSize(parseInt(e.target.value) || 1)}
              className="w-20 border border-[#e5e7eb] px-3 py-2 text-sm bg-white"
              data-testid="batch-size-input"
            />
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating || !config?.configured}
            className="ash-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="generate-batch-btn"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            {generating ? "Generating…" : "Generate batch with Claude"}
          </button>
          <p className="text-xs text-[#5A5F6A] flex-1 min-w-[200px]">
            Generates 1–6 newsletter drafts in one batch. Each draft is editable and only pushed to MailChimp on explicit approval.
          </p>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#5A5F6A]">
            <Loader2 className="animate-spin mr-2" size={18} /> Loading…
          </div>
        ) : newsletters.length === 0 ? (
          <div className="border border-dashed border-[#e5e7eb] p-12 text-center text-[#5A5F6A]" data-testid="empty-state">
            No newsletters yet. Click <strong>Generate batch with Claude</strong> to draft your first set.
          </div>
        ) : (
          <div className="border border-[#e5e7eb] bg-white" data-testid="newsletter-list">
            {newsletters.map((n, i) => (
              <button
                key={n.id}
                onClick={() => openNewsletter(n.id)}
                className={`w-full text-left px-5 py-4 flex flex-wrap items-center gap-4 hover:bg-[#F5F5F5] transition-colors ${
                  i < newsletters.length - 1 ? "border-b border-[#e5e7eb]" : ""
                }`}
                data-testid={`newsletter-row-${i}`}
              >
                <span
                  className={`inline-flex text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-1 border ${
                    STATUS_STYLES[n.status] || "bg-[#F5F5F5] text-[#3C4048] border-[#e5e7eb]"
                  }`}
                  data-testid={`status-pill-${i}`}
                >
                  {n.status}
                </span>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold text-[#080808] leading-snug">{n.subject_line || n.title}</div>
                  <div className="text-xs text-[#5A5F6A] mt-1 line-clamp-1">{n.preview_text}</div>
                </div>
                <div className="text-xs text-[#5A5F6A] inline-flex items-center gap-1.5">
                  <CalendarClock size={12} />
                  {n.created_at ? new Date(n.created_at).toLocaleDateString() : ""}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Detail / Editor */}
      {selectedId && selectedDoc && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end"
          onClick={closeDetail}
          data-testid="detail-overlay"
        >
          <div
            className="bg-white w-full max-w-3xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-[#e5e7eb] p-5 flex items-center justify-between sticky top-0 bg-white z-10">
              <button
                onClick={closeDetail}
                className="text-sm text-[#3C4048] inline-flex items-center gap-1.5"
                data-testid="close-detail"
              >
                <ArrowLeft size={14} /> Back to list
              </button>
              <span
                className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-1 border ${
                  STATUS_STYLES[selectedDoc.status] || ""
                }`}
              >
                {selectedDoc.status}
              </span>
            </div>

            <div className="p-6 space-y-5">
              <Field label="Subject line">
                <input
                  type="text"
                  value={editing.subject_line}
                  onChange={(e) => setEditing({ ...editing, subject_line: e.target.value })}
                  className="w-full border border-[#e5e7eb] px-3 py-2"
                  data-testid="edit-subject"
                />
              </Field>
              <Field label="Preview text (inbox snippet)">
                <input
                  type="text"
                  value={editing.preview_text}
                  onChange={(e) => setEditing({ ...editing, preview_text: e.target.value })}
                  className="w-full border border-[#e5e7eb] px-3 py-2"
                  data-testid="edit-preview"
                />
              </Field>
              <Field label="Internal title (admin only)">
                <input
                  type="text"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="w-full border border-[#e5e7eb] px-3 py-2"
                  data-testid="edit-title"
                />
              </Field>
              <Field label="Body (Markdown)">
                <textarea
                  rows={14}
                  value={editing.body_markdown}
                  onChange={(e) => setEditing({ ...editing, body_markdown: e.target.value })}
                  className="w-full border border-[#e5e7eb] px-3 py-2 font-mono text-sm leading-relaxed"
                  data-testid="edit-body"
                />
              </Field>

              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  onClick={saveEdits}
                  disabled={busy}
                  className="ash-btn-primary disabled:opacity-50"
                  data-testid="save-btn"
                >
                  Save edits
                </button>
                {selectedDoc.status === "Draft" && (
                  <button
                    onClick={() => doAction(newsletterApprove, "Approved.")}
                    disabled={busy}
                    className="ash-btn-outline inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="approve-btn"
                  >
                    <CheckCircle2 size={14} /> Approve
                  </button>
                )}
                {selectedDoc.status === "Draft" && (
                  <button
                    onClick={() => doAction(newsletterReject, "Rejected.")}
                    disabled={busy}
                    className="ash-btn-outline inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="reject-btn"
                  >
                    <XCircle size={14} /> Reject
                  </button>
                )}
                {(selectedDoc.status === "Approved" || selectedDoc.status === "Draft") &&
                  !selectedDoc.mailchimp_campaign_id && (
                    <PushControl
                      audiences={audiences}
                      defaultId={config?.default_audience_id}
                      onPush={doPush}
                      disabled={busy || !config?.configured}
                    />
                  )}
                {selectedDoc.mailchimp_campaign_id && selectedDoc.status === "Pushed" && (
                  <>
                    <button
                      onClick={doSchedule}
                      disabled={busy}
                      className="ash-btn-outline inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="schedule-btn"
                    >
                      <CalendarClock size={14} /> Schedule
                    </button>
                    <button
                      onClick={() =>
                        doAction(
                          newsletterSendNow,
                          "Sent.",
                          "Send NOW to the full audience? This cannot be undone."
                        )
                      }
                      disabled={busy}
                      className="ash-btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="send-now-btn"
                    >
                      <Send size={14} /> Send now
                    </button>
                  </>
                )}
                <button
                  onClick={doDelete}
                  disabled={busy}
                  className="ml-auto text-sm text-rose-700 hover:text-rose-900 inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="delete-btn"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>

              {selectedDoc.mailchimp_campaign_id && (
                <div className="mt-2 p-3 bg-sky-50 border border-sky-200 text-sky-900 text-xs">
                  <strong>MailChimp campaign:</strong>{" "}
                  <code>{selectedDoc.mailchimp_campaign_id}</code>
                  {" — "}
                  <a
                    href={`https://${
                      (config?.default_audience_id && "us1") || "us1"
                    }.admin.mailchimp.com/campaigns/`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-0.5 underline"
                    data-testid="mailchimp-link"
                  >
                    Open in MailChimp <ExternalLink size={11} />
                  </a>
                  {selectedDoc.scheduled_for && (
                    <div className="mt-1">
                      Scheduled for <strong>{selectedDoc.scheduled_for}</strong>
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-[#e5e7eb] pt-5">
                <span className="ash-eyebrow">Live HTML preview (rendered envelope)</span>
                <iframe
                  title="Newsletter preview"
                  srcDoc={selectedDoc.body_html}
                  className="w-full border border-[#e5e7eb] mt-3"
                  style={{ height: 600 }}
                  sandbox=""
                  data-testid="html-preview"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#5A5F6A] font-semibold mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}

function PushControl({ audiences, defaultId, onPush, disabled }) {
  const [chosen, setChosen] = useState(defaultId || "");
  return (
    <div className="inline-flex items-center gap-2">
      <select
        value={chosen}
        onChange={(e) => setChosen(e.target.value)}
        className="border border-[#e5e7eb] px-2 py-1 text-sm bg-white"
        data-testid="audience-select"
      >
        <option value="">— pick audience —</option>
        {audiences.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} ({a.member_count})
          </option>
        ))}
      </select>
      <button
        onClick={() => onPush(chosen)}
        disabled={disabled || !chosen}
        className="ash-btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
        data-testid="push-mc-btn"
      >
        <Send size={14} /> Push to MailChimp
      </button>
    </div>
  );
}
